import { toast } from "sonner";
import { kaltura } from "./client";
import type { KalturaAgentBlock, ScenarioSourceItem } from "@/lib/powtoon-be/types";
import type { KalturaCapabilityState } from "./capabilities";

/**
 * Sync the scenario's Kaltura avatar pair + agent + intellect.
 *
 * Kaltura's agent needs an avatar id, and an avatar is just a visual+voice
 * pair, so we mint one scenario-owned pair on the first save and update that
 * same pair in place afterwards — the scenario keeps its `avatar_id` for life
 * and no orphan avatars pile up in the catalog. Trainers never see these pairs.
 */
export async function syncKalturaAgent(input: {
  scenarioName: string;
  visual_id: string;
  voice_id: string;
  /** Pair provisioned by a previous save, if any. */
  existingAvatarId?: string;
  system_prompt: string;
  opening_text?: string;
  language?: string;
  inputs?: ScenarioSourceItem[];
  existingAgentId?: string;
  existingIntellectId?: number;
  /**
   * Complete upstream capability + knowledge-attach state, derived by
   * `kalturaCapabilities`. Always sent in full: Kaltura's `capabilities`
   * sub-dict is full-replace, so a partial write would drift.
   */
  capabilities: KalturaCapabilityState;
  /**
   * Presentation client-tool ids to attach to the intellect, in tool order.
   * Empty detaches — the list is full-replace upstream.
   */
  presentationToolIds?: string[];
}): Promise<KalturaAgentBlock> {
  const {
    scenarioName,
    visual_id,
    voice_id,
    existingAvatarId,
    system_prompt,
    opening_text,
    language,
    inputs,
    existingAgentId,
    existingIntellectId,
    capabilities,
  } = input;
  const presentationToolIds = input.presentationToolIds ?? [];

  if (!visual_id) {
    const msg = "Pick a face before saving this Kaltura scenario.";
    toast.error(msg);
    throw new Error(msg);
  }
  if (!voice_id) {
    const msg = "Pick a voice before saving this Kaltura scenario.";
    toast.error(msg);
    throw new Error(msg);
  }
  if (!system_prompt.trim()) {
    const msg = "Add persona / knowledge before saving this Kaltura scenario.";
    toast.error(msg);
    throw new Error(msg);
  }

  try {
    const avatar_id = await provisionAvatar({
      existingAvatarId,
      visual_id,
      voice_id,
      scenarioName,
      opening_text,
    });
    const res = existingAgentId
      ? await kaltura.updateAgent({
          agent_id: existingAgentId,
          intellect_id: existingIntellectId,
          name: scenarioName,
          avatar_id,
          system_prompt,
          opening_text,
          language,
          knowledge_id: capabilities.knowledge_id,
          web_search: capabilities.web_search,
          screen_share: capabilities.screen_share,
          tool_ids: presentationToolIds,
        })
      : await kaltura.createAgent({
          name: scenarioName,
          avatar_id,
          system_prompt,
          opening_text,
          language,
          web_search: capabilities.web_search,
          screen_share: capabilities.screen_share,
          tool_ids: presentationToolIds,
          ...(capabilities.knowledge_id ? { knowledge_id: capabilities.knowledge_id } : {}),
        });
    return {
      agent_id: res.agent_id || existingAgentId,
      intellect_id: res.intellect_id ?? existingIntellectId,
      app_guid: res.app_guid,
      avatar_id,
      visual_id,
      voice_id,
      system_prompt,
      opening_text,
      language,
      web_search_enabled: capabilities.web_search,
      screen_share_enabled: capabilities.screen_share,
      present_document: capabilities.present_document,
      presentation_tool_ids: presentationToolIds,
      inputs,
    };
  } catch (err) {
    const msg = (err as Error)?.message || "Could not sync agent to Kaltura";
    toast.error(`Save failed: ${msg}`);
    throw err;
  }
}

/**
 * Reuse-and-update the scenario's avatar, creating one only when the scenario
 * has none yet (or when Kaltura no longer knows the stored id).
 */
async function provisionAvatar(input: {
  existingAvatarId?: string;
  visual_id: string;
  voice_id: string;
  scenarioName: string;
  opening_text?: string;
}): Promise<string> {
  const { existingAvatarId, visual_id, voice_id, scenarioName, opening_text } = input;
  const create = async () =>
    (
      await kaltura.createAvatar({
        visual_id,
        voice_id,
        name: scenarioName,
        opening_phrase: opening_text,
      })
    ).avatar.avatar_id;

  if (!existingAvatarId) return create();

  try {
    const { avatar } = await kaltura.updateAvatar({
      avatar_id: existingAvatarId,
      visual_id,
      voice_id,
      name: scenarioName,
      opening_phrase: opening_text,
    });
    return avatar.avatar_id || existingAvatarId;
  } catch (err) {
    // The stored avatar was deleted on Kaltura's side — mint a replacement
    // rather than failing the whole save.
    const msg = (err as Error)?.message || "";
    if (/not found|404|no.?such|does not exist/i.test(msg)) return create();
    throw err;
  }
}